import type { StackEntry } from './types';
import { defaultSource, source } from './catalogue';

export interface ViewState {
  lon: number;
  lat: number;
  zoom: number;
}

export interface AppState {
  base: StackEntry;
  overlays: StackEntry[];
  compare: boolean;
  view: ViewState;
}

const PRAGUE: ViewState = { lon: 14.4213, lat: 50.0875, zoom: 14 };

type Listener = (s: AppState, reason: Reason) => void;
export type Reason = 'stack' | 'view' | 'init' | 'opacity';

let state: AppState = {
  base: { sourceId: defaultSource().id, opacity: 1 },
  overlays: [],
  compare: false,
  view: { ...PRAGUE },
};

const listeners = new Set<Listener>();

export function getState(): AppState {
  return state;
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(reason: Reason): void {
  for (const fn of listeners) fn(state, reason);
  scheduleHashWrite();
}

export function setBase(sourceId: string, timeId?: string): void {
  state = { ...state, base: { sourceId, timeId, opacity: 1 } };
  emit('stack');
}

export function setBaseTime(timeId: string | undefined): void {
  state = { ...state, base: { ...state.base, timeId } };
  emit('stack');
}

export function addOverlay(sourceId: string, timeId?: string): void {
  if (state.overlays.some((o) => o.sourceId === sourceId)) return;
  const def = source(sourceId);
  const opacity = def?.defaultOpacity ?? 1;
  state = { ...state, overlays: [...state.overlays, { sourceId, timeId, opacity }] };
  emit('stack');
}

export function removeOverlay(sourceId: string): void {
  state = { ...state, overlays: state.overlays.filter((o) => o.sourceId !== sourceId) };
  emit('stack');
}

export function toggleOverlay(sourceId: string, timeId?: string): void {
  if (state.overlays.some((o) => o.sourceId === sourceId)) removeOverlay(sourceId);
  else addOverlay(sourceId, timeId);
}

export function setOpacity(sourceId: string, opacity: number): void {
  const clamp = Math.min(1, Math.max(0, opacity));
  if (state.base.sourceId === sourceId) {
    state = { ...state, base: { ...state.base, opacity: clamp } };
  } else {
    state = {
      ...state,
      overlays: state.overlays.map((o) => (o.sourceId === sourceId ? { ...o, opacity: clamp } : o)),
    };
  }
  // Not 'stack': rebuilding the HUD here would destroy the slider being dragged.
  emit('opacity');
}

export function setOverlayTime(sourceId: string, timeId: string | undefined): void {
  state = {
    ...state,
    overlays: state.overlays.map((o) => (o.sourceId === sourceId ? { ...o, timeId } : o)),
  };
  emit('stack');
}

export function setCompare(on: boolean): void {
  if (state.compare === on) return;
  state = { ...state, compare: on };
  emit('stack');
}

export function setView(view: ViewState): void {
  state = { ...state, view };
  emit('view');
}

/** The layer whose epoch the time strip and opacity slider act on: topmost first. */
export function activeEntry(): StackEntry {
  return state.overlays.length ? state.overlays[state.overlays.length - 1] : state.base;
}

// ---------------------------------------------------------------- permalink

function encodeEntry(e: StackEntry): string {
  let s = e.sourceId;
  if (e.timeId) s += `:${e.timeId}`;
  if (e.opacity < 1) s += `@${Math.round(e.opacity * 100)}`;
  return s;
}

function decodeEntry(raw: string): StackEntry | undefined {
  const m = /^([^:@]+)(?::([^@]+))?(?:@(\d+))?$/.exec(raw);
  if (!m || !source(m[1])) return undefined;
  return {
    sourceId: m[1],
    timeId: m[2],
    opacity: m[3] ? Math.min(100, Number(m[3])) / 100 : 1,
  };
}

let writing = false;
let hashTimer: ReturnType<typeof setTimeout> | undefined;

/** Coalesce rapid updates (slider drags, map panning) into one history write. */
function scheduleHashWrite(): void {
  if (hashTimer !== undefined) return;
  hashTimer = setTimeout(() => {
    hashTimer = undefined;
    writeHash();
  }, 200);
}

export function writeHash(): void {
  const { view, base, overlays, compare } = state;
  const specs = [base, ...overlays].map(encodeEntry).join(',');
  const parts = [
    view.zoom.toFixed(2),
    view.lat.toFixed(5),
    view.lon.toFixed(5),
    specs,
  ];
  if (compare) parts.push('c');
  const hash = `#/${parts.join('/')}`;
  if (hash === location.hash) return;
  writing = true;
  history.replaceState(null, '', hash);
  writing = false;
}

export function readHash(): boolean {
  const m = /^#\/([\d.]+)\/(-?[\d.]+)\/(-?[\d.]+)\/([^/]+)(?:\/(c))?$/.exec(location.hash);
  if (!m) return false;
  const entries = m[4].split(',').map(decodeEntry).filter(Boolean) as StackEntry[];
  if (!entries.length) return false;
  state = {
    base: entries[0],
    overlays: entries.slice(1),
    compare: m[5] === 'c',
    view: { zoom: Number(m[1]), lat: Number(m[2]), lon: Number(m[3]) },
  };
  return true;
}

export function initFromHash(): void {
  readHash();
  for (const fn of listeners) fn(state, 'init');
  writeHash();
  window.addEventListener('hashchange', () => {
    if (writing) return;
    if (readHash()) for (const fn of listeners) fn(state, 'init');
  });
}
